import { parseInstanceMarker } from '../../../portal/instanceMarker';
import { formatValueWithUnit } from '../../../utils/format';
import type { ExportCell, ExportTable } from '../../widget-actions';
import type { BoxFigure, NodeId, NodePath, OwnMinWidth, ProportionNode } from './NestedProportionBar.types';

/**
 * The keys of every node under `root`, depth-first — the order a reader meets the boxes, and the
 * order the palette is handed out in.
 *
 * `root` itself is left out: it is the frame its children are measured against, not one of them,
 * so it keeps the plain border colour and never spends a palette colour.
 */
export const flatProportionNodeKeys = (root: ProportionNode): string[] =>
  (root.children ?? []).flatMap((child) => [child.key, ...flatProportionNodeKeys(child)]);

/** The legend as one line of text, for the figure's accessible name. */
export const legendText = (legend: string[] = []) =>
  legend.map((value) => parseInstanceMarker(value) ?? value).join(', ');

/**
 * The tree as a grid: one row per box, depth-first, the order the bar is read.
 *
 * `level` and `parent` are what carry the nesting into a flat file. The shares the boxes print
 * are left out: they are the value over the root's, and the printed ones are rounded to whole
 * percent, so a reader recomputing them from this file gets a better answer than a column would.
 */
export const buildProportionTable = ({
  name,
  root,
  unit,
}: {
  name: string;
  root: ProportionNode;
  unit?: string;
}): ExportTable => {
  const nodes: { node: ProportionNode; level: number; parent: string | null }[] = [];
  const walk = (node: ProportionNode, level: number, parent: string | null) => {
    nodes.push({ node, level, parent });
    for (const child of node.children ?? []) walk(child, level + 1, node.label);
  };
  walk(root, 0, null);

  // A column of nothing but blanks is worse than no column at all.
  const hasDescriptions = nodes.some(({ node }) => node.description);

  return {
    name,
    columns: [
      'level',
      'label',
      'parent',
      'value',
      ...(unit ? ['unit'] : []),
      ...(hasDescriptions ? ['description'] : []),
    ],
    rows: nodes.map(({ node, level, parent }): ExportCell[] => [
      level,
      node.label,
      parent,
      node.value,
      ...(unit ? [unit] : []),
      ...(hasDescriptions ? [node.description ?? null] : []),
    ]),
  };
};

/** Room for a share, `100%` included; the label hides below it. */
const MIN_BOX_WIDTH_PX = 52;
const BOX_BORDER_AND_PADDING_PX = 2 * (1 + 4);
const CHILDREN_GAP_PX = 2;
/** One character of a box's figure, near enough for a floor. */
const FIGURE_CHAR_PX = 8;
const FIGURE_PADDING_PX = 2 * 4;

const boxWidthForFigure = (figure: string) =>
  Math.max(MIN_BOX_WIDTH_PX, FIGURE_CHAR_PX * figure.length + FIGURE_PADDING_PX + BOX_BORDER_AND_PADDING_PX);

const fitsAnyShare: OwnMinWidth = () => MIN_BOX_WIDTH_PX;

/** A share always fits the floor; a value and its unit set their own. */
export const ownMinWidthFor = (figure: BoxFigure, unit?: string): OwnMinWidth =>
  figure === 'value' ? (node) => boxWidthForFigure(formatValueWithUnit(node.value, unit)) : fitsAnyShare;

export const nodeIdOf = (path: NodePath): NodeId => JSON.stringify(path);

const sumOf = (values: number[]) => values.reduce((total, value) => total + value, 0);

export const hasChildren = (node: ProportionNode) => (node.children?.length ?? 0) > 0;

/**
 * Each child's share of its parent's width. Children summing past their parent share all of it in
 * proportion to each other, while their labels keep the shares the payload sent.
 */
export const childWidthShares = (node: ProportionNode): number[] => {
  const values = (node.children ?? []).map((child) => Math.max(0, child.value));
  const widthBasis = Math.max(node.value, sumOf(values));
  return values.map((value) => (widthBasis > 0 ? value / widthBasis : 0));
};

/** Its own floor, or its children's floors side by side when it is not folded. */
export const minBoxWidth = (
  node: ProportionNode,
  path: NodePath,
  foldedIds: ReadonlySet<NodeId> = new Set(),
  ownMinWidth: OwnMinWidth = fitsAnyShare,
): number => {
  const children = node.children ?? [];
  if (children.length === 0 || foldedIds.has(nodeIdOf(path))) return ownMinWidth(node);
  const childrenWidth = sumOf(
    children.map((child) => minBoxWidth(child, [...path, child.key], foldedIds, ownMinWidth)),
  );
  return Math.max(
    ownMinWidth(node),
    childrenWidth + CHILDREN_GAP_PX * (children.length - 1) + BOX_BORDER_AND_PADDING_PX,
  );
};

/** Flexbox's sharing of a row: by `shares`, except a box under its min width is pinned there. */
const distributeRowWidth = (shares: number[], minWidths: number[], rowWidth: number): number[] => {
  const isPinnedAtMin = shares.map(() => false);
  for (;;) {
    const pinnedWidth = sumOf(minWidths.filter((_, i) => isPinnedAtMin[i]));
    const unpinnedShare = sumOf(shares.filter((_, i) => !isPinnedAtMin[i]));
    const proportionalWidth = (i: number) =>
      unpinnedShare > 0 ? ((rowWidth - pinnedWidth) * shares[i]) / Math.max(1, unpinnedShare) : 0;
    const tooNarrow = shares.findIndex((_, i) => !isPinnedAtMin[i] && proportionalWidth(i) < minWidths[i]);
    if (tooNarrow === -1) return shares.map((_, i) => (isPinnedAtMin[i] ? minWidths[i] : proportionalWidth(i)));
    isPinnedAtMin[tooNarrow] = true;
  }
};

/**
 * The boxes under `root`, at `rootPath`, to fold at `rootWidthPx`: those whose share is narrower
 * than their children need, then, smallest first, as many as a row needs to fit its parent.
 */
export const findFoldedIds = (
  root: ProportionNode,
  rootPath: NodePath,
  rootWidthPx: number,
  ownMinWidth: OwnMinWidth = fitsAnyShare,
): Set<NodeId> => {
  const foldedIds = new Set<NodeId>();

  const foldChildrenThatDoNotFit = (node: ProportionNode, path: NodePath, widthPx: number) => {
    const children = node.children ?? [];
    if (children.length === 0) return;
    const rowWidthPx = widthPx - BOX_BORDER_AND_PADDING_PX - CHILDREN_GAP_PX * (children.length - 1);
    const shares = childWidthShares(node);
    const childPaths = children.map((child) => [...path, child.key]);
    const childMinWidths = () =>
      children.map((child, index) => minBoxWidth(child, childPaths[index], foldedIds, ownMinWidth));
    const foldableIndexes = children.flatMap((child, index) => (hasChildren(child) ? [index] : []));

    for (const index of foldableIndexes) {
      const fullWidth = minBoxWidth(children[index], childPaths[index], new Set(), ownMinWidth);
      if (rowWidthPx * shares[index] < fullWidth) foldedIds.add(nodeIdOf(childPaths[index]));
    }
    for (const index of foldableIndexes.sort((a, b) => children[a].value - children[b].value)) {
      if (sumOf(childMinWidths()) <= rowWidthPx) break;
      foldedIds.add(nodeIdOf(childPaths[index]));
    }

    const childWidths = distributeRowWidth(shares, childMinWidths(), rowWidthPx);
    for (const [index, child] of children.entries()) {
      if (!foldedIds.has(nodeIdOf(childPaths[index])))
        foldChildrenThatDoNotFit(child, childPaths[index], childWidths[index]);
    }
  };

  foldChildrenThatDoNotFit(root, rootPath, rootWidthPx);
  return foldedIds;
};

export const countDescendants = (node: ProportionNode): number =>
  sumOf((node.children ?? []).map((child) => 1 + countDescendants(child)));

/** The nodes `path` walks through from `root`, both ends included; empty when it leaves the tree. */
export const findNodesAlongPath = (root: ProportionNode, path: NodePath): ProportionNode[] => {
  if (path[0] !== root.key) return [];
  const nodes = [root];
  for (const key of path.slice(1)) {
    const next = nodes[nodes.length - 1].children?.find((child) => child.key === key);
    if (!next) return [];
    nodes.push(next);
  }
  return nodes;
};
